/**
 * The composer under a ticket's conversation (TMMPDM-0109): an internal note by default, or a reply
 * to the member, which tmm-api emails to them (TMMAPI-0181 item 31). The choice is explicit and the
 * button names it, so a note is never sent as a reply. After a post the composer goes back to an
 * internal note, and the page re-reads the ticket. Errors appear here, in place.
 */
import { useId, useState } from 'react';
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { errorCopy } from '@/api/errors';
import type { TicketVisibility } from '@/api/tmm/types';
import { BODY_MAX, isNotDeployed, notAvailableText } from './tickets';

export function TicketComposer({ number, onPosted }: { number: number; onPosted: () => void }) {
  const { t } = useCopy();
  const labelId = useId();
  const headingId = useId();
  const [visibility, setVisibility] = useState<TicketVisibility>('INTERNAL');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const publicReply = visibility === 'PUBLIC';

  const post = async () => {
    const trimmed = body.trim();
    if (trimmed === '') {
      setFieldError(t('tickets.composer.required', 'Write something to post.'));
      return;
    }
    if (trimmed.length > BODY_MAX) {
      setFieldError(
        t('tickets.composer.tooLong', 'At most {{max, number}} characters.', { max: BODY_MAX }),
      );
      return;
    }
    setBusy(true);
    setError(null);
    setFieldError(null);
    try {
      await adminApi.postTicketUpdate(number, { visibility, body: trimmed });
      setBody('');
      setVisibility('INTERNAL');
      onPosted();
    } catch (failure: unknown) {
      setError(
        isNotDeployed(failure)
          ? notAvailableText(t)
          : errorCopy(failure, t, t('tickets.composer.error', "Couldn't post it.")),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    // Inside the Conversation card (h2): its own h3, set off by a rule rather than a second card.
    <Box
      role="group"
      aria-labelledby={headingId}
      sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}
    >
      <Typography id={headingId} variant="h3" component="h3" sx={{ mb: 1.5 }}>
        {t('tickets.composer.title', 'Add to the conversation')}
      </Typography>
      <Stack spacing={2}>
        <FormControl>
          <FormLabel id={labelId}>{t('tickets.composer.visibility', 'Who sees it')}</FormLabel>
          <RadioGroup
            row
            aria-labelledby={labelId}
            value={visibility}
            onChange={(event) =>
              setVisibility(event.target.value === 'PUBLIC' ? 'PUBLIC' : 'INTERNAL')
            }
          >
            <FormControlLabel
              value="INTERNAL"
              control={<Radio />}
              label={t('tickets.composer.internal', 'Internal note')}
            />
            <FormControlLabel
              value="PUBLIC"
              control={<Radio />}
              label={t('tickets.composer.public', 'Reply to the member')}
            />
          </RadioGroup>
        </FormControl>
        {publicReply ? (
          <Alert severity="warning">
            {t('tickets.composer.publicWarning', 'The member is emailed this reply.')}
          </Alert>
        ) : (
          <Alert severity="info">
            {t('tickets.internalNote', 'Internal note: the member never sees this.')}
          </Alert>
        )}
        <TextField
          multiline
          minRows={3}
          label={
            publicReply
              ? t('tickets.composer.replyLabel', 'Reply')
              : t('tickets.composer.noteLabel', 'Note')
          }
          value={body}
          onChange={(event) => {
            setBody(event.target.value);
            if (fieldError) setFieldError(null);
          }}
          error={fieldError !== null || body.length > BODY_MAX}
          helperText={
            fieldError ??
            t('tickets.composer.counter', '{{used, number}} of {{max, number}} characters', {
              used: body.length,
              max: BODY_MAX,
            })
          }
        />
        {error ? <Alert severity="error">{error}</Alert> : null}
        <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
          <SubmitButton busy={busy} onClick={() => void post()}>
            {publicReply
              ? t('tickets.composer.postReply', 'Post reply to the member')
              : t('tickets.composer.postNote', 'Post internal note')}
          </SubmitButton>
        </Stack>
      </Stack>
    </Box>
  );
}
